// Add deal, a page of its own (module 10 U285 to U289, U293, U295, U297; ADD-DEAL-BRIEF-2026-10-05.md sections 4 to 6;
// CLIENT-WORDS-AND-VALUE-BRIEF-2026-10-05.md section 4; REQ-2026-10-05-17, the Deal work lane's W6). The drawing is
// concept B as revised after Mike's notes of 08:51 (screens/2026-10-05-add-deal-concepts/b2-form.html, b2-more.html,
// with the lines the form says back on a-more.html). Module 10 DESIGN B2130.
//
// Mike, 5 Oct 2026 08:15 CEST: "In truth this should be 'Add Deal' A logical form should be there that is based around
// our company records and product verticals including also rights." /deals/new: "Selling to a client" or "Acquiring
// rights"; the form in the main column, what Cobrain holds beside it (the company on record, its conversations filed
// under no deal to tick, its latest lines; a new company's domain line; the property on the register). Company, product
// (or property), stage, keenness, owner and the next step with its date are needed before Add (U287, U293), and the lead
// says which is missing rather than greying without words. After Add the page goes back to Deals opened on the new line,
// where the Added line and its Undo wait (AddedLine.tsx).
//
// The choices are read once when the page opens (GET /api/deals/new-form); the column when a company is chosen; a new
// company's address as it is typed (a POST, so the address is never in a logged URL). The server checks everything again
// (apps/api/src/deals/work/add-deal.ts).
import { ActionInput, ActionSelect, ActionTextarea, ActionButton } from '../parts/index.js';
import { useViewing } from '../view-as.js';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { PageProps } from '../records/shared.js';
import { classWord } from '../records/shared.js';
import { parkedWords } from '../deals/live-words.js';
import { narrow, type PickerOption } from '../find/Picker.js';
import { NOTE_ACCEPT, notesApi } from '../notes/api.js';
import { uploadedWords } from '../notes/words.js';
import {
  AddDealError, dealWorkApi, DealWorkError,
  type AddressCheck, type CompanyColumn, type NewDealBody, type OpenDeal, type NewForm, type NewFormProperty, type PayBasis, type PayBody, type Period,
} from './api.js';
import {
  addedWords, addressWords, amountOf, BASIS_WORDS, BLANK_PAY, boxWords, CURRENCY_SIGN, dayWords, expectWords, fromWords, leadWords,
  noteAdded, OTHER, ownerWord, payOf, SHARE_OF_WORDS, summaryWords, workedYear, type PayBox, type PayForm,
} from './addDeal.js';
import '../deals/deals.css';
import './dealwork.css';

/** At most this many names under a picker at once; the rest are reached by typing. */
export const PICKER_ROWS = 8;
/** How long the address waits for the typing to stop before it is checked. */
const ADDRESS_WAIT_MS = 400;
const SAID_MAX = 1000;
const STEP_MAX = 200;

type Side = 'sales' | 'rights';
type Who = { kind: 'company'; id: string; name: string } | { kind: 'new' };
interface Row { stage: string; keenness: number | null; ownerId: string }
type Prop = { kind: 'reg'; id: string } | { kind: 'new'; name: string; sport: string; territory: string };
type Origin = '' | 'pitched' | 'enquiry' | 'referral';
type Where = '' | 'show' | 'meeting' | 'call' | 'mail';
type RightsBasis = 'unknown' | 'year' | 'match' | 'share';
/** The field a refusal points at, so it is marked. */
type Field = 'company' | 'name' | 'email' | 'product' | 'property' | 'rights' | 'stage' | 'keenness' | 'owner' | 'step' | 'by' | 'pay';

const SIGNED = 'signed';
const SIGNED_LINE = 'A signed deal needs its contract: add it at Contracting, and mark it Signed from its card once the contract is in.';
const NOT_COMMERCIAL = 'Only Commercial and admins add deals. Ask an admin to add you.';
const NOT_SAVED = 'Not added: Cobrain could not save it, and nothing changed.';
/** The rights side's foot, as drawn (a-more.html), kept on Mike's pop-up answer of 5 Oct 2026: "Show it as drawn". */
export const RIGHTS_FOOT = 'Once added, the deal lists the operators we sell it to.';

const ORIGIN_WORDS = (side: Side): Array<[Origin, string]> => [['pitched', 'We pitched'], ['enquiry', side === 'rights' ? 'Offered to us' : 'They asked'], ['referral', 'A referral']];
const WHERE_WORDS: Array<[Where, string]> = [['show', 'at a show'], ['meeting', 'at a meeting'], ['call', 'on a call'], ['mail', 'by mail']];
const PERIOD_WORDS: Array<[Period, string]> = [['year', 'a year'], ['month', 'a month']];

/** Today in Mike's time, as a date box wants it: the next step is never due before today. */
function todayIso(now = new Date()): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${g('year')}-${g('month')}-${g('day')}`;
}

/** A picker: a box that narrows a list as it is typed, with "Add <typed> as a new ..." last unless a row is so named. */
function Picker({ label, options, placeholder, newWords, onChoose, invalid }: {
  label: string; options: readonly PickerOption[]; placeholder: string;
  newWords: ((typed: string) => string) | null;
  onChoose: (o: { id: string; name: string } | { typed: string }) => void;
  invalid?: boolean;
}) {
  const viewing = useViewing();
  const [typed, setTyped] = useState('');
  const [at, setAt] = useState(0);
  const id = useId();
  const t = typed.replace(/\s+/g, ' ').trim();
  const rows: Array<{ id: string; name: string } | { typed: string }> = [];
  if (t) {
    for (const o of narrow(options, t, PICKER_ROWS).shown) rows.push({ id: o.id, name: o.name });
    if (newWords && !options.some((o) => o.name.toLowerCase() === t.toLowerCase())) rows.push({ typed: t });
  }
  useEffect(() => { setAt(0); }, [typed]);
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (viewing) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter' && rows[at]) { e.preventDefault(); onChoose(rows[at]!); }
    else if (e.key === 'Escape') { e.preventDefault(); setTyped(''); }
  };
  return (
    <div className="dw-combo">
      <ActionInput type="text" role="combobox" aria-label={label} placeholder={placeholder} value={typed} className={invalid ? 'bad' : undefined}
        autoComplete="off" spellCheck={false} aria-expanded={rows.length > 0} aria-controls={`${id}-l`} aria-autocomplete="list"
        {...(rows.length > 0 ? { 'aria-activedescendant': `${id}-o${at}` } : {})}
        onChange={(e) => setTyped(e.target.value)} onKeyDown={keys} />
      {rows.length > 0
        ? (
          <ul id={`${id}-l`} role="listbox" aria-label={label}>
            {rows.map((r, i) => (
              <li key={'id' in r ? r.id : 'new'} id={`${id}-o${i}`} role="option" aria-selected={i === at} aria-disabled={viewing ? true : undefined}
                className={[i === at ? 'at' : '', 'typed' in r ? 'new' : ''].filter(Boolean).join(' ') || undefined}
                onMouseEnter={() => setAt(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => { if (!viewing) onChoose(r); }}>
                {'typed' in r ? newWords!(r.typed) : r.name}
              </li>
            ))}
          </ul>
        )
        : null}
    </div>
  );
}

/** A field: its label above, a hint below. */
function Fld({ label, opt, full, children, hint, id }: { label: ReactNode; opt?: string; full?: boolean; children: ReactNode; hint?: ReactNode; id?: string }) {
  return (
    <div className={full ? 'dwn-fld full' : 'dwn-fld'} {...(id ? { id } : {})}>
      <span className="dwn-l">{label}{opt ? <> <em>{opt}</em></> : null}</span>
      {children}
      {hint ? <span className="dwn-hint">{hint}</span> : null}
    </div>
  );
}

/** One of a few, pressed: the switch, the pay basis, exclusive. */
function Seg<T extends string>({ label, value, options, onChange, wide }: { label: string; value: T | null; options: Array<[T, string]>; onChange: (v: T) => void; wide?: boolean }) {
  return (
    <div className={wide ? 'dwn-seg wide' : 'dwn-seg'} role="radiogroup" aria-label={label}>
      {options.map(([v, w]) => (
        <ActionButton key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? 'on' : undefined} onClick={() => onChange(v)}>{w}</ActionButton>
      ))}
    </div>
  );
}

/** Keenness, 5 to 1 in navy (U269), chosen by the person adding (U293). */
function Keen({ value, onChange, small, invalid, label }: { value: number | null; onChange: (k: number) => void; small?: boolean; invalid?: boolean; label: string }) {
  return (
    // lv-kpick is the card's keenness buttons (deals.css), the one place navy is worn on a button (keen-tokens.test.ts).
    <div className={['lv-kpick', 'dwn-keens', small ? 'sm' : '', invalid ? 'bad' : ''].filter(Boolean).join(' ')} role="radiogroup" aria-label={label}>
      {[5, 4, 3, 2, 1].map((k) => (
        <ActionButton key={k} type="button" role="radio" aria-checked={value === k} aria-label={`Keenness ${k}`} className={value === k ? `on k${k}` : undefined} onClick={() => onChange(k)}>{k}</ActionButton>
      ))}
    </div>
  );
}

function Ladder({ stages, value, onChange, onSigned }: { stages: NewForm['stages']; value: string; onChange: (code: string) => void; onSigned: () => void }) {
  return (
    <div className="dwn-ladder" role="radiogroup" aria-label="Stage">
      {stages.map((s) => (
        <ActionButton key={s.code} type="button" role="radio" aria-checked={value === s.code} className={value === s.code ? 'on' : undefined}
          onClick={() => (s.code === SIGNED ? onSigned() : onChange(s.code))}>{s.name}</ActionButton>
      ))}
    </div>
  );
}

function OwnerSelect({ team, value, onChange, me, label }: { team: NewForm['team']; value: string; onChange: (id: string) => void; me: string | null; label: string }) {
  return (
    <ActionSelect aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {team.map((p) => <option key={p.principalId} value={p.principalId}>{p.principalId === me ? `You, ${p.name}` : p.name}</option>)}
    </ActionSelect>
  );
}

/** A box for an amount, with its currency's sign before it. */
function Amount({ label, value, onChange, currency, after, invalid, suffix, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; currency?: string | undefined; after?: ReactNode; invalid?: boolean; suffix?: string | undefined;
  placeholder?: string | undefined;
}) {
  return (
    <span className={invalid ? 'dwn-amt bad' : 'dwn-amt'}>
      {currency ? <span className="sg" aria-hidden="true">{CURRENCY_SIGN[currency] ?? currency}</span> : null}
      <ActionInput type="text" inputMode="decimal" aria-label={label} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {suffix ? <span className="sg" aria-hidden="true">{suffix}</span> : null}
      {after}
    </span>
  );
}

function PeriodSelect({ label, value, onChange }: { label: string; value: Period; onChange: (p: Period) => void }) {
  return (
    <ActionSelect className="dwn-per" aria-label={label} value={value} onChange={(e) => onChange(e.target.value as Period)}>
      {PERIOD_WORDS.map(([v, w]) => <option key={v} value={v}>{w}</option>)}
    </ActionSelect>
  );
}

/**
 * One product's Expected revenues: how it is paid, the boxes that basis needs and no others, and what we expect to earn
 * this year, worked out from the terms until a figure is typed over it (U295). With several products each block is
 * headed by its product and its boxes are named for it.
 */
function PayFields({ value: f, onChange, product, invalid }: {
  value: PayForm; onChange: (x: Partial<PayForm>) => void; product: string | null; invalid: (b: PayBox) => boolean;
}) {
  const of = (l: string) => (product ? `${l} (${product})` : l);
  const read = payOf(f);
  const shown = 'pay' in read ? read.pay : null;
  const expectLine = expectWords(shown);
  /** The figure the terms give, shown in the box until a figure is typed over it. */
  const worked = shown ? workedYear(f) : null;
  const shareWord = SHARE_OF_WORDS[f.shareOf] ?? 'revenue';
  const box = (b: PayBox, extra: { currency?: string; suffix?: string; after?: ReactNode; placeholder?: string } = {}) => (
    <Amount label={of(boxWords(b, f.shareOf))} value={f[b]} onChange={(v) => onChange({ [b]: v })} invalid={invalid(b)}
      currency={extra.currency} suffix={extra.suffix} after={extra.after} placeholder={extra.placeholder} />
  );
  const currencySelect = (
    <ActionSelect className="dwn-per" aria-label={of('Currency')} value={f.currency} onChange={(e) => onChange({ currency: e.target.value })}>
      {Object.keys(CURRENCY_SIGN).map((c) => <option key={c} value={c}>{c}</option>)}
    </ActionSelect>
  );
  return (
    <div className={product ? 'dwn-payfor' : undefined} role={product ? 'group' : undefined} aria-label={product ? of('Expected revenues') : undefined}>
      {product ? <h4 className="dwn-ph">{product}</h4> : null}
      <Seg wide label={of('Expected revenues')} value={f.basis} onChange={(b) => onChange({ basis: b })}
        options={(['fixed', 'share', 'per_match', 'unknown'] as PayBasis[]).map((b) => [b, BASIS_WORDS[b]])} />
      {f.basis && f.basis !== 'unknown'
        ? (
          <div className="dwn-grid dwn-pays">
            {f.basis === 'fixed'
              ? <Fld label="Fee">{box('fee', { currency: f.currency, after: <PeriodSelect label={of('Fee period')} value={f.feePeriod} onChange={(p) => onChange({ feePeriod: p })} /> })}</Fld>
              : null}
            {f.basis === 'share'
              ? (
                <>
                  <Fld label="Share">{box('sharePct', { suffix: '%' })}</Fld>
                  <Fld label="Of">
                    <ActionSelect aria-label={of('Share of')} value={f.shareOf} onChange={(e) => onChange({ shareOf: e.target.value })}>
                      {Object.entries(SHARE_OF_WORDS).map(([v, w]) => <option key={v} value={v}>{w}</option>)}
                    </ActionSelect>
                  </Fld>
                </>
              )
              : null}
            {f.basis === 'per_match'
              ? (
                <>
                  <Fld label="Rate a match">{box('matchRate', { currency: f.currency })}</Fld>
                  <Fld label="Matches a year" opt="if known">{box('matchCount')}</Fld>
                </>
              )
              : null}
            {f.basis !== 'fixed'
              ? <Fld label="Minimum guarantee" opt="as proposed">{box('minimum', { currency: f.currency, after: <PeriodSelect label={of('Minimum period')} value={f.minimumPeriod} onChange={(p) => onChange({ minimumPeriod: p })} /> })}</Fld>
              : null}
            {f.basis === 'share'
              ? <Fld label={`Potential ${shareWord} share`} opt="on top of the minimum">{box('above', { currency: f.currency, after: <PeriodSelect label={of('Potential share period')} value={f.abovePeriod} onChange={(p) => onChange({ abovePeriod: p })} /> })}</Fld>
              : null}
            <Fld full label={<>What we expect to earn this year <span className="dwn-chip">Estimate</span></>}
              hint={f.basis === 'share' ? `Cobrain adds the potential ${shareWord} share to the minimum.` : undefined}>
              <span className="dwn-pair">
                {box('expected', { currency: f.currency, placeholder: worked !== null ? Math.round(worked).toLocaleString('en-GB') : 'a year' })}
                {currencySelect}
              </span>
            </Fld>
            {expectLine
              ? <p className="dwn-note full"><b>{expectLine}</b> Cobrain keeps it up to date from statements, mail, Teams and recorded calls, and says when it moves it.</p>
              : null}
          </div>
        )
        : null}
    </div>
  );
}

/** A message filed under another deal is ticked by its own key, apart from its thread's. */
const elsewhereKey = (signalId: string): string => `msg:${signalId}`;

/**
 * The conversations ticked: a thread's messages filed under no deal when the thread is ticked, and each message filed
 * under another deal that is ticked of its own. One conversation each, however many of its messages are ticked.
 */
export function tickedConversations(col: CompanyColumn | null, ticked: ReadonlySet<string>): Array<{ thread: string; file: string[] }> {
  if (!col) return [];
  return col.threads.flatMap((t) => {
    const file = [...(ticked.has(t.thread) ? t.signalIds : []), ...t.elsewhere.filter((e) => ticked.has(elsewhereKey(e.signalId))).map((e) => e.signalId)];
    return file.length > 0 ? [{ thread: t.thread, file }] : [];
  });
}

/**
 * BUGS.md D110: what the conversations column leaves out, in words, and its button: "3 more of the last 18 months, and 12
 * older." with "Show 15 more", or null when it lists them all.
 */
export function moreWords(col: Pick<CompanyColumn, 'threads' | 'threadsMore'>): { words: string; button: string } | null {
  const m = col.threadsMore;
  if (!m || m.recent + m.older === 0) return null;
  const n = m.recent + m.older;
  const parts = [m.recent > 0 ? `${m.recent} more of the last 18 months` : null, m.older > 0 ? `${m.older} older` : null].filter(Boolean);
  if (col.threads.length === 0) return { words: `${m.older} older ${m.older === 1 ? 'does' : 'do'}.`, button: `Show ${m.older === 1 ? 'it' : 'them'}` };
  return { words: `${parts.join(', and ')}.`, button: `Show ${n} more` };
}

/**
 * BUGS.md D108: a parked deal said as Deals says it (deals/live-words.ts parkedWords), "Parked till 19 Oct", with its
 * reason after a colon where `why`; null on an open one, which reads its stage, or Open.
 */
export function parkedOf(d: Pick<OpenDeal, 'lifecycle' | 'pausedUntil' | 'stateReason'>, why: boolean): string | null {
  return parkedWords({ lifecycle: d.lifecycle ?? 'open', pausedUntil: d.pausedUntil ?? null, stateReason: why ? d.stateReason ?? null : null })?.words ?? null;
}

/** "2 products open", and ", 1 parked" when any of them is: a parked deal is not counted open (BUGS.md D108). */
export function openWords(open: ReadonlyArray<Pick<OpenDeal, 'lifecycle'>>): string {
  const parked = open.filter((d) => d.lifecycle === 'paused').length;
  const n = open.length - parked;
  return `${n} ${n === 1 ? 'product' : 'products'} open${parked > 0 ? `, ${parked} parked` : ''}`;
}

/** BUGS.md D131: how many of the holder's other properties are listed beside the form; the page says "6 of N shown." past it. */
export const HOLDER_SHOWN = 6;

/** The column beside the form for a company on record: its facts, its open deals, what is filed under none, its latest. */
function CompanySide({ col, ticked, onTick, onMore }: { col: CompanyColumn; ticked: ReadonlySet<string>; onTick: (thread: string) => void; onMore: () => Promise<void> }) {
  const c = col.company;
  const [more, setMore] = useState<'idle' | 'busy' | 'failed'>('idle');
  const left = moreWords(col);
  const showMore = () => { setMore('busy'); onMore().then(() => setMore('idle'), () => setMore('failed')); };
  return (
    <>
      <section className="dwn-side" aria-label={`${c.name} on record`}>
        <h3>{c.name} on record</h3>
        <div className="dwn-facts">
          {c.type ? <span><b>{c.type}</b></span> : null}
          <span>{classWord(c.class)}</span>
          {c.owner ? <span>Account owner {c.owner}</span> : <span>No account owner</span>}
          {c.domains.map((d) => <span key={d}>{d}</span>)}
          {c.agreements > 0 ? <span>{c.agreements} {c.agreements === 1 ? 'agreement' : 'agreements'} in force</span> : null}
        </div>
        {col.open.length > 0
          ? (
            <ul className="dwn-dl">
              {col.open.map((d) => (
                <li key={d.dealId}><span className="nm">{d.product ?? d.title}</span><span className="st">{parkedOf(d, true) ?? d.stage}{d.owner ? `, ${d.owner}` : ', no owner'}</span></li>
              ))}
            </ul>
          )
          : null}
      </section>
      <section className="dwn-side" id="dwn-convs" aria-label="Conversations not filed under any deal">
        <h3>Conversations not filed under any deal</h3>
        {col.threads.length === 0
          ? <p className="dwn-note">{left ? `No conversation with ${c.name} in the last 18 months sits outside a deal.` : `No conversations with ${c.name} sit outside a deal.`}</p>
          : (
            <>
              <span className="dwn-hint">
                Tick what belongs to this one.
                {col.threads.some((t) => t.elsewhere.length > 0) ? ' A message already filed under another deal stays there when you file it here too.' : ''}
              </span>
              <ul className="dwn-convs">
                {col.threads.map((t) => (
                  <li key={t.thread}>
                    <label>
                      <ActionInput type="checkbox" checked={ticked.has(t.thread)} onChange={() => onTick(t.thread)} />
                      <span className="body">
                        <span className="t">{t.title || '(no subject)'}</span>
                        <span className="m">
                          {t.who ? <span>{t.who}</span> : null}
                          <span>{dayWords(t.at)}</span>
                          {t.meeting ? <span>Meeting</span> : null}
                          {t.items > 1 ? <span>{t.items} messages</span> : null}
                        </span>
                      </span>
                    </label>
                    {/* The thread's messages already filed under another deal, each with that deal's name, unticked
                        (pipeline FT1, BUGS.md D32): ticked, it is filed here as well and stays there. */}
                    {t.elsewhere.length > 0
                      ? (
                        <ul className="dwn-else">
                          {t.elsewhere.map((e) => (
                            <li key={e.signalId}>
                              <label>
                                <ActionInput type="checkbox" checked={ticked.has(elsewhereKey(e.signalId))} onChange={() => onTick(elsewhereKey(e.signalId))} />
                                <span className="body">
                                  <span className="t">{e.subject || '(no subject)'}</span>
                                  <span className="m"><span>{dayWords(e.at)}</span><span>Filed under {e.dealTitle}</span></span>
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )
                      : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        {left
          ? (
            <p className="dwn-note">
              {left.words}{' '}
              <ActionButton reading type="button" className="hx-link" disabled={more === 'busy'} onClick={showMore}>{left.button}</ActionButton>
              {more === 'failed' ? <span role="alert"> They did not load. Try again.</span> : null}
            </p>
          )
          : col.threadsCapped ? <p className="dwn-note">The newest 200 are listed.</p> : null}
      </section>
      {col.latest.length > 0
        ? (
          <section className="dwn-side" aria-label={`Latest from ${c.name}`}>
            <h3>Latest from {c.name}</h3>
            <ul className="dwn-dl">
              {col.latest.map((l, i) => <li key={i}><span className="nm">{l.said}</span><span className="st">{[dayWords(l.at), l.kind].filter(Boolean).join(', ')}</span></li>)}
            </ul>
          </section>
        )
        : null}
    </>
  );
}

const WILL_HOLD: Array<[string, (n: string) => string]> = [
  ['Conversations', () => 'Mail and meetings with the domain'],
  ['People', () => 'The address you gave, then whoever writes from there'],
  ['Usage', (n) => `Once their usage is matched to ${n}`],
  ['Money', () => 'Once finance bills them'],
  ['News', () => 'Stories naming either name'],
  ['Incidents', (n) => `Problems logged against ${n}`],
];

export function AddDealPage({ store, open }: PageProps) {
  const tenant = store.tenantId;
  const [form, setForm] = useState<NewForm | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [side, setSide] = useState<Side>('sales');
  const [who, setWho] = useState<Who | null>(null);
  const [nc, setNc] = useState({ name: '', brand: '', email: '', phone: '' });
  const [col, setCol] = useState<{ id: string; data: CompanyColumn | null; failed: boolean } | null>(null);
  const [addr, setAddr] = useState<{ key: string; check: AddressCheck } | null>(null);
  const [fams, setFams] = useState<string[]>([]);
  const [other, setOther] = useState('');
  const [base, setBase] = useState<Row>({ stage: 'talking', keenness: null, ownerId: '' });
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [prop, setProp] = useState<Prop | null>(null);
  const [video, setVideo] = useState(false);
  const [data, setData] = useState(false);
  const [exclusive, setExclusive] = useState<'yes' | 'no' | 'unknown'>('unknown');
  const [territory, setTerritory] = useState('');
  const [termFrom, setTermFrom] = useState('');
  const [termUntil, setTermUntil] = useState('');
  const [step, setStep] = useState('');
  const [by, setBy] = useState('');
  const [contactId, setContactId] = useState('');
  const [origin, setOrigin] = useState<Origin>('');
  const [where, setWhere] = useState<Where>('');
  const [showId, setShowId] = useState('');
  const [said, setSaid] = useState('');
  // Expected revenues on the sales side: each product its own, keyed by its family; '' before a product is chosen (B2251).
  const [payForms, setPayForms] = useState<Readonly<Record<string, PayForm>>>({});
  // The rights side's one fee, what we pay the holder.
  const [currency, setCurrency] = useState('GBP');
  const [fee, setFee] = useState('');
  const [shareOf, setShareOf] = useState('ngr');
  const [rBasis, setRBasis] = useState<RightsBasis>('unknown');
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<{ text: string; tone: 'plain' | 'warn'; go?: { words: string; act: () => void } } | null>(null);
  const [signedLine, setSignedLine] = useState(false);
  const [noteLines, setNoteLines] = useState<Array<{ key: string; text: string; tone: 'plain' | 'warn' }>>([]);
  const files = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLElement | null>(null);
  /** Counts the presses of Add refused for something missing; each takes the person to the field (U320). */
  const [refused, setRefused] = useState(0);
  const today = todayIso();

  // A refused Add takes the person to the first field it marked, the cursor in it, as well as saying it in red by the
  // button: Mike, by pop-up, 5 Oct 2026, "Yes, take me to it" (U320, B2131). The mark is drawn by the render the press
  // caused, so the field is found after it.
  useEffect(() => {
    if (refused === 0) return;
    const marked = formRef.current?.querySelector<HTMLElement>('.bad');
    if (!marked) return;
    const field = marked.matches('input, select, textarea, button') ? marked : marked.querySelector<HTMLElement>('input, select, textarea, button');
    const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof marked.scrollIntoView === 'function') marked.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' });
    field?.focus({ preventScroll: true });
  }, [refused]);

  useEffect(() => {
    let live = true;
    setForm(null); setLoadErr(null);
    dealWorkApi.newForm(tenant)
      .then((f) => {
        if (!live) return;
        setForm(f);
        if (f.me) setBase((b) => (b.ownerId ? b : { ...b, ownerId: f.me!.principalId }));
        // "Add a product" on an opened account and on the company record open the page with the company set (?company=).
        const preset = new URLSearchParams(window.location.search).get('company');
        const c = preset ? f.companies.find((x) => x.id === preset) : undefined;
        if (c) setWho({ kind: 'company', id: c.id, name: c.name });
      })
      .catch((e: unknown) => { if (live) setLoadErr(e instanceof DealWorkError ? e.message : 'The page did not load. Nothing was changed.'); });
    return () => { live = false; };
  }, [tenant]);

  // The column for a company on record, read when it is chosen.
  const companyId = who?.kind === 'company' ? who.id : null;
  useEffect(() => {
    setTicked(new Set()); setContactId('');
    if (!companyId) { setCol(null); return; }
    let live = true;
    setCol({ id: companyId, data: null, failed: false });
    dealWorkApi.newFormCompany(tenant, companyId)
      .then((d) => { if (live) setCol({ id: companyId, data: d, failed: false }); })
      .catch(() => { if (live) setCol({ id: companyId, data: null, failed: true }); });
    return () => { live = false; };
  }, [tenant, companyId]);

  // BUGS.md D110: every loose thread, of any age, in place of the newest eight; the ticks are kept by thread.
  const moreThreads = (): Promise<void> => {
    if (!companyId) return Promise.resolve();
    const id = companyId;
    return dealWorkApi.newFormThreads(tenant, id).then((r) => {
      setCol((c) => (c && c.id === id && c.data ? { ...c, data: { ...c.data, threads: r.threads, threadsMore: { recent: 0, older: 0 }, threadsCapped: r.capped } } : c));
    });
  };

  // A new company's address, checked once the typing stops (U288).
  const addrKey = `${nc.email.trim().toLowerCase()}|${nc.name.trim()}|${nc.brand.trim()}`;
  useEffect(() => {
    if (who?.kind !== 'new' || !/@[^@\s]+\.[^@\s]+$/.test(nc.email.trim())) { setAddr(null); return; }
    let live = true;
    const t = window.setTimeout(() => {
      dealWorkApi.checkAddress(tenant, nc.email.trim(), nc.name.trim(), nc.brand.trim())
        .then((check) => { if (live) setAddr({ key: addrKey, check }); })
        .catch(() => { if (live) setAddr(null); });
    }, ADDRESS_WAIT_MS);
    return () => { live = false; window.clearTimeout(t); };
  }, [tenant, who?.kind, addrKey, nc.email, nc.name, nc.brand]);

  const me = form?.me?.principalId ?? null;
  const nameOf = (id: string): string | null => form?.team.find((p) => p.principalId === id)?.name ?? null;
  const stageName = (code: string): string => form?.stages.find((s) => s.code === code)?.name ?? code;
  const column = col && col.data && companyId === col.id ? col.data : null;
  const openFam = (f: string) => column?.openFamilies[f] ?? null;
  const several = side === 'sales' && fams.length > 1;
  const rowOf = (f: string): Row => (several ? rows[f] ?? { ...base, keenness: null } : base);
  const setRow = (f: string, r: Partial<Row>) => setRows((x) => ({ ...x, [f]: { ...rowOf(f), ...r } }));
  const property: NewFormProperty | null = prop?.kind === 'reg' ? form?.properties.find((p) => p.id === prop.id) ?? null : null;
  const holderId = side === 'rights' && who?.kind === 'company' ? who.id : null;
  // The holder's other properties on the register, in its order: six listed, and "6 of N shown." past them (BUGS.md D131).
  const more = holderId ? (form?.properties ?? []).filter((p) => p.holderId === holderId && p.id !== property?.id) : [];

  const toggleFam = (f: string) => {
    const o = openFam(f);
    if (o) { open(`/deals?open=${encodeURIComponent(o.dealId ?? o.accountId)}`); return; }
    setRefusal(null);
    if (fams.includes(f)) {
      const next = fams.filter((x) => x !== f);
      // Back to one product: its own row becomes the one the single controls set.
      if (next.length === 1 && fams.length > 1) setBase(rows[next[0]!] ?? base);
      setFams(next);
      return;
    }
    // The first product chosen takes the Expected revenues typed before it was.
    if (fams.length === 0) setPayForms((m) => { const { '': first, ...rest } = m; return first ? { ...rest, [f]: rest[f] ?? first } : m; });
    if (fams.length === 1) setRows((x) => ({ ...x, [fams[0]!]: x[fams[0]!] ?? base, [f]: { ...base, keenness: null } }));
    else if (fams.length > 1) setRows((x) => ({ ...x, [f]: { ...base, keenness: null } }));
    setFams([...fams, f]);
  };

  /** The rights side's fee, what we pay the holder; 'bad' when the box is not a number. */
  const rightsPay = (): PayBody | null | 'bad' => {
    const f = amountOf(fee);
    if (Number.isNaN(f)) return 'bad';
    if (rBasis === 'unknown') return f === null ? null : { basis: 'unknown', currency };
    if (f === null) return null;
    if (rBasis === 'year') return { basis: 'fixed', currency, fee: f, feePeriod: 'year' };
    if (rBasis === 'match') return { basis: 'per_match', currency, matchRate: f };
    return { basis: 'share', currency, sharePct: f, shareOf };
  };
  const rPay = side === 'rights' ? rightsPay() : null;
  const payForm = (k: string): PayForm => payForms[k] ?? BLANK_PAY;
  const setPayForm = (k: string, x: Partial<PayForm>) => setPayForms((m) => ({ ...m, [k]: { ...(m[k] ?? BLANK_PAY), ...x } }));
  /** The sales side's Expected revenues: one block a product chosen, or one before any is. */
  const payKeys = side === 'sales' ? (fams.length > 0 ? fams : ['']) : [];
  const payRead = payKeys.map((k) => ({ key: k, read: payOf(payForm(k)) }));
  const payBad = payRead.find((x) => 'bad' in x.read) as { key: string; read: { bad: PayBox } } | undefined;
  const linePay = (k: string): PayBody | null => { const r = payRead.find((x) => x.key === k)?.read; return r && 'pay' in r ? r.pay : null; };

  const lineKeys = side === 'rights' ? ['rights'] : fams;
  const products = side === 'rights'
    ? [property?.name ?? (prop?.kind === 'new' ? prop.name.trim() : '')].filter(Boolean)
    : fams.map((f) => (f === OTHER ? other.trim() || OTHER : f));
  const ticks = tickedConversations(column, ticked);
  const companyName = who?.kind === 'company' ? who.name : nc.name.trim();
  const addrSays = who?.kind === 'new' && addr && addr.key === addrKey ? addressWords(addr.check, nc.name, nc.brand) : null;

  /** The first thing missing before Add, in the words the lead says, and the field it points at. */
  const missing = (): { field: Field; words: string; key?: string; box?: PayBox } | null => {
    if (!form) return { field: 'company', words: 'Reading the companies and products' };
    if (!who) return { field: 'company', words: side === 'rights' ? 'Choose the rights holder first.' : 'Choose the company first.' };
    if (who.kind === 'new') {
      if (!nc.name.trim()) return { field: 'name', words: 'Give the company\'s name first.' };
      if (!nc.email.trim()) return { field: 'email', words: 'Give an email address at the company first.' };
      if (addrSays?.blocks) return { field: 'email', words: `Use ${addrSays.use!.name}, or change the address, first.` };
    }
    if (side === 'sales') {
      if (fams.length === 0) return { field: 'product', words: 'Choose a product first.' };
      if (fams.includes(OTHER) && !other.trim()) return { field: 'product', words: 'Name the product first.' };
    } else {
      if (!prop || (prop.kind === 'new' && (!prop.name.trim() || !prop.sport.trim()))) return { field: 'property', words: prop?.kind === 'new' ? 'Give the property\'s sport and name first.' : 'Choose the property first.' };
      if (!video && !data) return { field: 'rights', words: 'Say which rights first: video, data or both.' };
    }
    if (lineKeys.some((k) => rowOf(k).keenness === null)) return { field: 'keenness', words: 'Choose a keenness first.' };
    if (!step.trim()) return { field: 'step', words: 'Add a next step first.' };
    if (!by) return { field: 'by', words: 'Choose the date of the next step first.' };
    if (rPay === 'bad') return { field: 'pay', words: 'The fee is not a number.' };
    if (payBad) {
      const k = payBad.key;
      const product = several ? (k === OTHER ? other.trim() || OTHER : k) : null;
      return { field: 'pay', key: k, box: payBad.read.bad, words: `"${boxWords(payBad.read.bad, payForm(k).shareOf)}" under Expected revenues${product ? ` for ${product}` : ''} is not a number.` };
    }
    return null;
  };
  const gap = missing();
  const bad = (f: Field) => tried && gap?.field === f;
  /** The one pay box a refused Add points at, so it alone is marked. */
  const badBox = (k: string) => (b: PayBox) => tried && gap?.field === 'pay' && gap.key === k && gap.box === b;

  const summary = summaryWords({
    company: companyName,
    deals: lineKeys.map((k, i) => ({ product: products[i] ?? '', stage: stageName(rowOf(k).stage), owner: ownerWord(rowOf(k).ownerId, me, nameOf(rowOf(k).ownerId)) })).filter((d) => d.product),
    ticked: ticks.length,
  });

  const body = (): NewDealBody | null => {
    if (!who || gap || rPay === 'bad') return null;
    return {
      side,
      company: who.kind === 'company' ? { id: who.id } : { new: { name: nc.name.trim(), brand: nc.brand.trim() || null, email: nc.email.trim(), phone: nc.phone.trim() || null } },
      lines: side === 'rights'
        ? [{ family: null, product: null, stage: base.stage, keenness: base.keenness!, ownerId: base.ownerId }]
        : fams.map((f) => ({ family: f, product: f === OTHER ? other.trim() : f, stage: rowOf(f).stage, keenness: rowOf(f).keenness!, ownerId: rowOf(f).ownerId, pay: linePay(f) })),
      rights: side === 'rights' && prop
        ? {
          ...(prop.kind === 'reg' ? { propertyId: prop.id } : { newProperty: { sport: prop.sport.trim(), name: prop.name.trim(), territory: prop.territory.trim() || null } }),
          video, data, exclusive, territory: territory.trim() || null, termFrom: termFrom || null, termUntil: termUntil || null,
        }
        : null,
      step: step.trim(), by, contactId: who.kind === 'company' && contactId ? contactId : null,
      origin: origin || null, where: where || null, showId: where === 'show' && showId ? showId : null,
      said: side === 'sales' && said.trim() ? said.trim() : null,
      pay: side === 'rights' ? rPay : null, file: ticks.flatMap((t) => t.file),
    };
  };

  const add = () => {
    setTried(true); setRefusal(null); setSignedLine(false);
    const b = body();
    if (!b) { if (gap) setRefused((r) => r + 1); return; }
    if (busy) return;
    setBusy(true);
    dealWorkApi.addDeal(tenant, b)
      .then((a) => {
        noteAdded({ store: tenant, added: a, words: addedWords(a, stageName, ticks.length) });
        open(`/deals?open=${encodeURIComponent(a.deals[0]?.dealId ?? a.accountId)}`);
      })
      .catch((e: unknown) => {
        setBusy(false);
        if (!(e instanceof AddDealError)) { setRefusal({ text: NOT_SAVED, tone: 'warn', go: { words: 'Try again', act: add } }); return; }
        if (e.status === 403) { setRefusal({ text: NOT_COMMERCIAL, tone: 'plain' }); return; }
        if (e.open) { const o = e.open; setRefusal({ text: e.message, tone: 'plain', go: { words: 'Go to it', act: () => open(`/deals?open=${encodeURIComponent(o.dealId ?? o.accountId)}`) } }); return; }
        if (e.company) { const c = e.company; setRefusal({ text: e.message, tone: 'plain', go: { words: `Use ${c.name}`, act: () => { setWho({ kind: 'company', id: c.id, name: c.name }); setRefusal(null); } } }); return; }
        if (e.status >= 500 || e.status === 0) { setRefusal({ text: NOT_SAVED, tone: 'warn', go: { words: 'Try again', act: add } }); return; }
        setRefusal({ text: e.message.charAt(0).toUpperCase() + e.message.slice(1), tone: 'warn' });
      });
  };

  const attach = async (picked: File[]) => {
    if (picked.length === 0) return;
    const out: typeof noteLines = [];
    for (let i = 0; i < picked.length; i += 1) {
      const f = picked[i]!;
      let w: { text: string; tone: 'plain' | 'warn' };
      try { w = uploadedWords(f.name, await notesApi.upload(tenant, f, { meeting: null, company: companyId })); }
      catch { w = { text: `${f.name}: it was not uploaded. Try again.`, tone: 'warn' }; }
      out.push({ key: `${i}-${f.name}`, ...w });
      setNoteLines([...out]);
    }
  };

  const switchSide = (s: Side) => {
    if (s === side) return;
    setSide(s); setRefusal(null); setTried(false);
    // The rights side offers rights holders first; a client chosen on the sales side is kept only if it holds rights.
    if (who?.kind === 'company' && s === 'rights' && !form?.companies.find((c) => c.id === who.id)?.rightsHolder) setWho(null);
    setProp(null);
  };

  const companies: PickerOption[] = useMemo(() => {
    const cs = form?.companies ?? [];
    const sorted = side === 'rights' ? [...cs.filter((c) => c.rightsHolder), ...cs.filter((c) => !c.rightsHolder)] : cs;
    return sorted.map((c) => ({ id: c.id, name: c.name }));
  }, [form, side]);
  const propertyOptions: PickerOption[] = useMemo(() => {
    const ps = form?.properties ?? [];
    const mine = holderId ? ps.filter((p) => p.holderId === holderId) : [];
    const rest = ps.filter((p) => !mine.includes(p));
    return [...mine, ...rest].map((p) => ({ id: p.id, name: p.name }));
  }, [form, holderId]);

  if (loadErr) return <div className="deals dwn"><Head open={open} side={side} onSide={switchSide} /><p className="dl-err" role="alert">{loadErr}</p></div>;
  if (!form) return <div className="deals dwn"><Head open={open} side={side} onSide={switchSide} /><p className="dwn-note" role="status">Reading the companies and products</p></div>;
  if (!form.mayAdd) return <div className="deals dwn"><Head open={open} side={side} onSide={switchSide} /><p className="dwn-note" role="status">{NOT_COMMERCIAL}</p></div>;

  const n = lineKeys.length;
  const one = rowOf(lineKeys[0] ?? '');

  const whoBlock = (
    <>
      <h3 className="dwn-h">Who</h3>
      {who?.kind === 'new'
        ? (
          <div className="dwn-grid">
            <Fld label="Company name"><ActionInput type="text" aria-label="Company name" value={nc.name} maxLength={200} className={bad('name') ? 'bad' : undefined} onChange={(e) => setNc({ ...nc, name: e.target.value })} /></Fld>
            <Fld label="Brand name"><ActionInput type="text" aria-label="Brand name" value={nc.brand} maxLength={200} onChange={(e) => setNc({ ...nc, brand: e.target.value })} /></Fld>
            <Fld label="Email address"><ActionInput type="email" aria-label="Email address" value={nc.email} maxLength={254} autoComplete="off" className={bad('email') ? 'bad' : undefined} onChange={(e) => setNc({ ...nc, email: e.target.value })} /></Fld>
            <Fld label="Phone" opt="if you have it"><ActionInput type="tel" aria-label="Phone" value={nc.phone} maxLength={40} placeholder="+44 20 7946 0000" onChange={(e) => setNc({ ...nc, phone: e.target.value })} /></Fld>
            {addrSays
              ? (
                <p className="dwn-note full" role="status">
                  {addrSays.use ? <b>{addrSays.text.split('. ')[0]}.</b> : null}
                  {addrSays.use ? ` ${addrSays.text.split('. ').slice(1).join('. ')}` : addrSays.text}
                  {addrSays.use ? <ActionButton type="button" className="hx-link go" onClick={() => setWho({ kind: 'company', id: addrSays.use!.id, name: addrSays.use!.name })}>Use {addrSays.use.name}</ActionButton> : null}
                </p>
              )
              : null}
            <p className="full dwn-back"><ActionButton type="button" className="hx-link m" onClick={() => setWho(null)}>Choose a company on record instead</ActionButton></p>
          </div>
        )
        : (
          <div className="dwn-grid">
            <Fld full label={side === 'rights' ? 'Rights holder' : 'Company'}>
              {who
                ? <span className="dwn-picked"><span>{who.name}</span><ActionButton type="button" className="hx-link m" onClick={() => setWho(null)}>Change</ActionButton></span>
                : (
                  <Picker label={side === 'rights' ? 'Rights holder' : 'Company'} options={companies} invalid={bad('company')}
                    placeholder={side === 'rights' ? 'Find a rights holder, or type a new one' : 'Find a company, or type a new one'}
                    newWords={(t) => `Add "${t}" as a new company`}
                    onChoose={(o) => { setRefusal(null); if ('typed' in o) { setNc({ name: o.typed, brand: '', email: '', phone: '' }); setWho({ kind: 'new' }); } else setWho({ kind: 'company', id: o.id, name: o.name }); }} />
                )}
              {column
                ? (
                  <span className="dwn-facts phone-only">
                    <span><b>{classWord(column.company.class)}</b></span>
                    {column.company.owner ? <span>Account owner {column.company.owner}</span> : null}
                    <span>{openWords(column.open)}</span>
                    {column.threads.length > 0
                      ? <ActionButton reading type="button" className="hx-link" onClick={() => document.getElementById('dwn-convs')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })}>{column.threads.length} {column.threads.length === 1 ? 'conversation' : 'conversations'} to file</ActionButton>
                      : null}
                  </span>
                )
                : null}
            </Fld>
          </div>
        )}
    </>
  );

  const stageAndNext = (
    <div className="dwn-grid">
      {!several
        ? (
          <Fld full label="Stage">
            <Ladder stages={form.stages} value={one.stage} onChange={(c) => { setSignedLine(false); setBase({ ...base, stage: c }); }} onSigned={() => setSignedLine(true)} />
            {signedLine ? <span className="dwn-hint">{SIGNED_LINE}</span> : null}
          </Fld>
        )
        : null}
      <Fld label="Next step" hint={column?.suggested && step.trim() !== column.suggested.step
        ? <>{fromWords(column.suggested.at, column.suggested.kind)}: {column.suggested.step} <ActionButton type="button" className="hx-link" onClick={() => setStep(column.suggested!.step.slice(0, STEP_MAX))}>Use it</ActionButton></>
        : undefined}>
        <ActionInput type="text" aria-label="Next step" value={step} maxLength={STEP_MAX} placeholder="What we do next" className={bad('step') ? 'bad' : undefined} onChange={(e) => setStep(e.target.value)} />
      </Fld>
      <Fld label="By"><ActionInput type="date" aria-label="By" value={by} min={today} className={bad('by') ? 'bad' : undefined} onChange={(e) => setBy(e.target.value)} /></Fld>
      {!several
        ? (
          <>
            <Fld full label="Keenness" opt="5 is keenest"
              hint={bad('keenness') ? <span className="warnword">Choose a keenness to add the deal.</span> : 'Choose one; you know them best today. After that Cobrain keeps it up to date from what they say and do, and says when it moves it.'}>
              <Keen label="Keenness" value={one.keenness} invalid={bad('keenness')} onChange={(k) => setBase({ ...base, keenness: k })} />
            </Fld>
            <Fld label="Owner"><OwnerSelect label="Owner" team={form.team} me={me} value={one.ownerId} onChange={(id) => setBase({ ...base, ownerId: id })} /></Fld>
          </>
        )
        : null}
      {side === 'sales'
        ? (
          <Fld label="Their contact" opt="if you know">
            {who?.kind === 'new'
              ? <span className="dwn-fixed">{nc.email.trim() || 'The address you give'}</span>
              : (
                <ActionSelect aria-label="Their contact" value={contactId} onChange={(e) => setContactId(e.target.value)} disabled={!column}>
                  <option value="">{column && column.people.length === 0 ? 'No one on record yet' : 'Choose'}</option>
                  {(column?.people ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.email ? `, ${p.email}` : ''}</option>)}
                </ActionSelect>
              )}
          </Fld>
        )
        : null}
      <Fld label="How it came in" full={side === 'rights' || several}>
        <span className="dwn-pair">
          <ActionSelect aria-label="How it came in" value={origin} onChange={(e) => setOrigin(e.target.value as Origin)}>
            <option value="">Choose</option>
            {ORIGIN_WORDS(side).map(([v, w]) => <option key={v} value={v}>{w}</option>)}
          </ActionSelect>
          <ActionSelect aria-label="Where it came in" value={where} onChange={(e) => setWhere(e.target.value as Where)}>
            <option value="">where</option>
            {WHERE_WORDS.map(([v, w]) => <option key={v} value={v}>{w}</option>)}
          </ActionSelect>
          {where === 'show'
            ? (
              <ActionSelect aria-label="Which show" value={showId} onChange={(e) => setShowId(e.target.value)}>
                <option value="">Which show</option>
                {form.shows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </ActionSelect>
            )
            : null}
        </span>
      </Fld>
      {side === 'sales'
        ? (
          <Fld full label="What they said" opt="in their words; it starts the record of the conversation">
            <ActionTextarea aria-label="What they said" rows={2} value={said} maxLength={SAID_MAX} placeholder="Their words" onChange={(e) => setSaid(e.target.value)} />
          </Fld>
        )
        : null}
    </div>
  );

  const rowsTable = several
    ? (
      <>
        <span className="dwn-hint">You choose the stage and keenness of each; Cobrain keeps them up to date after.</span>
        <div className="dwn-tablewrap">
          <table className="dwn-rows">
            <thead><tr><th scope="col">Product</th><th scope="col">Stage</th><th scope="col">Keenness</th><th scope="col">Owner</th></tr></thead>
            <tbody>
              {fams.map((f, i) => {
                const r = rowOf(f);
                return (
                  <tr key={f}>
                    <th scope="row">{products[i]}</th>
                    <td>
                      <ActionSelect aria-label={`Stage of ${products[i]}`} value={r.stage} onChange={(e) => setRow(f, { stage: e.target.value })}>
                        {form.stages.filter((s) => s.code !== SIGNED).map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                      </ActionSelect>
                    </td>
                    <td><Keen small label={`Keenness of ${products[i]}`} value={r.keenness} invalid={bad('keenness') && r.keenness === null} onChange={(k) => setRow(f, { keenness: k })} /></td>
                    <td><OwnerSelect label={`Owner of ${products[i]}`} team={form.team} me={me} value={r.ownerId} onChange={(id) => setRow(f, { ownerId: id })} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {bad('keenness') ? <span className="dwn-hint warnword">Choose a keenness for each to add them.</span> : null}
      </>
    )
    : null;

  const salesWhat = (
    <>
      <h3 className="dwn-h">What</h3>
      <Fld full label="Product" opt="one or more; each becomes its own deal">
        <div className={bad('product') ? 'dwn-prods bad' : 'dwn-prods'} role="group" aria-label="Products">
          {form.families.map((f) => {
            const o = openFam(f);
            const on = fams.includes(f);
            // BUGS.md D108: a parked one says so, and till when, where it read Open.
            const state = o ? parkedOf(o, false) ?? 'Open' : null;
            return (
              <ActionButton reading={Boolean(o)} key={f} type="button" aria-pressed={o ? undefined : on}
                aria-label={o ? `${f}: ${state!.charAt(0).toLowerCase()}${state!.slice(1)}, ${o.owner ? `${o.owner}'s` : 'no owner'}. Go to it` : undefined}
                className={o ? 'open' : on ? 'on' : undefined} onClick={() => toggleFam(f)}>
                {f}{o ? <small>{state}{o.owner ? `, ${o.owner}` : ''}</small> : on && column ? <small>New</small> : null}
              </ActionButton>
            );
          })}
        </div>
      </Fld>
      {fams.includes(OTHER)
        ? <Fld label="Which product"><ActionInput type="text" aria-label="Which product" value={other} maxLength={120} placeholder="Name the product" onChange={(e) => setOther(e.target.value)} /></Fld>
        : null}
    </>
  );

  const rightsWhat = (
    <>
      <h3 className="dwn-h">What</h3>
      <div className="dwn-grid">
        <Fld full label="Property">
          {prop?.kind === 'reg' && property
            ? <span className="dwn-picked"><span>{property.name}</span><ActionButton type="button" className="hx-link m" onClick={() => setProp(null)}>Change</ActionButton></span>
            : prop?.kind === 'new'
              ? (
                <span className="dwn-trio">
                  <ActionInput type="text" aria-label="Sport" placeholder="Sport" value={prop.sport} maxLength={80} className={bad('property') && !prop.sport.trim() ? 'bad' : undefined} onChange={(e) => setProp({ ...prop, sport: e.target.value })} />
                  <ActionInput type="text" aria-label="Competition" placeholder="Competition" value={prop.name} maxLength={200} className={bad('property') && !prop.name.trim() ? 'bad' : undefined} onChange={(e) => setProp({ ...prop, name: e.target.value })} />
                  <ActionInput type="text" aria-label="Its territory" placeholder="Its territory" value={prop.territory} maxLength={200} onChange={(e) => setProp({ ...prop, territory: e.target.value })} />
                  <ActionButton type="button" className="hx-link m" onClick={() => setProp(null)}>Choose from the register instead</ActionButton>
                </span>
              )
              : (
                <Picker label="Property" options={propertyOptions} invalid={bad('property')} placeholder="Find a property on the register, or type a new one"
                  newWords={(t) => `Add "${t}" as a new property`}
                  onChoose={(o) => setProp('typed' in o ? { kind: 'new', name: o.typed, sport: '', territory: '' } : { kind: 'reg', id: o.id })} />
              )}
        </Fld>
        <Fld label="Which rights">
          <div className={bad('rights') ? 'dwn-prods bad' : 'dwn-prods'} role="group" aria-label="Which rights">
            <ActionButton type="button" aria-pressed={video} className={video ? 'on' : undefined} onClick={() => setVideo(!video)}>Video</ActionButton>
            <ActionButton type="button" aria-pressed={data} className={data ? 'on' : undefined} onClick={() => setData(!data)}>Data</ActionButton>
          </div>
        </Fld>
        <Fld label="Exclusive"><Seg label="Exclusive" value={exclusive} onChange={setExclusive} options={[['yes', 'Yes'], ['no', 'No'], ['unknown', 'Not known']]} /></Fld>
        <Fld label="Territory"><ActionInput type="text" aria-label="Territory" value={territory} maxLength={200} placeholder="Where we may sell it" onChange={(e) => setTerritory(e.target.value)} /></Fld>
        <Fld label="Term">
          <span className="dwn-pair">
            <ActionInput type="date" aria-label="Term from" value={termFrom} onChange={(e) => setTermFrom(e.target.value)} />
            <span className="dwn-to">to</span>
            <ActionInput type="date" aria-label="Term until" value={termUntil} min={termFrom || undefined} onChange={(e) => setTermUntil(e.target.value)} />
          </span>
        </Fld>
        <Fld label="Fee" opt="if known">
          <Amount label="Fee" value={fee} onChange={setFee} currency={rBasis === 'share' ? undefined : currency} suffix={rBasis === 'share' ? '%' : undefined} invalid={bad('pay')}
            after={rBasis === 'share' ? undefined : <ActionSelect className="dwn-per" aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>{Object.keys(CURRENCY_SIGN).map((c) => <option key={c} value={c}>{c}</option>)}</ActionSelect>} />
        </Fld>
        <Fld label="On what basis">
          <span className="dwn-pair">
            <ActionSelect aria-label="On what basis" value={rBasis} onChange={(e) => setRBasis(e.target.value as RightsBasis)}>
              <option value="unknown">Not known yet</option><option value="year">A year</option><option value="match">A match</option><option value="share">A share of revenue</option>
            </ActionSelect>
            {rBasis === 'share'
              ? <ActionSelect aria-label="A share of" value={shareOf} onChange={(e) => setShareOf(e.target.value)}>{Object.entries(SHARE_OF_WORDS).map(([v, w]) => <option key={v} value={v}>{w}</option>)}</ActionSelect>
              : null}
          </span>
        </Fld>
      </div>
    </>
  );

  // Expected revenues (Mike, 7 Oct 2026: "how it pays is naff, change this for expected revenues"): one block a product
  // chosen, each its own deal's terms ("there needs to be one per deal"), the product named over it when there are
  // several (B2251).
  const pays = side === 'sales'
    ? (
      <>
        <h3 className="dwn-h">Expected revenues</h3>
        {payKeys.map((k, i) => (
          <PayFields key={k || 'none'} value={payForm(k)} onChange={(x) => setPayForm(k, x)} product={several ? products[i] ?? k : null} invalid={badBox(k)} />
        ))}
      </>
    )
    : null;

  const foot = (
    <div className="dwn-foot">
      {/* Always pressable: pressed before it is ready, it says what is missing and marks the field (the brief's checklist). */}
      <ActionButton type="button" className="dl-opt lead" onClick={add}>{busy ? 'Adding…' : leadWords(Math.max(n, 1), who?.kind === 'new')}</ActionButton>
      <ActionButton reading type="button" className="dl-opt" onClick={() => open('/deals')}>Never mind</ActionButton>
      {tried && gap
        ? <span className="dwn-says warnword" role="alert">{gap.words}</span>
        : side === 'rights' ? <span className="dwn-says">{RIGHTS_FOOT}</span> : summary ? <span className="dwn-says">{summary}</span> : null}
      <ActionInput ref={files} type="file" multiple accept={NOTE_ACCEPT} hidden onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.target.value = ''; void attach(fs); }} />
      <ActionButton type="button" className="hx-link m right" onClick={() => files.current?.click()}>Attach notes or a photo</ActionButton>
      {refusal
        ? (
          <p className={refusal.tone === 'warn' ? 'dwn-note warn full' : 'dwn-note full'} role="alert">
            {refusal.text}
            {refusal.go ? <ActionButton type="button" className="hx-link go" onClick={refusal.go.act}>{refusal.go.words}</ActionButton> : null}
          </p>
        )
        : null}
      {noteLines.length > 0
        ? <ul className="nt-said full" role="status">{noteLines.map((l) => <li key={l.key} className={l.tone === 'warn' ? 'warn' : undefined}>{l.text}</li>)}</ul>
        : null}
    </div>
  );

  const aside = (
    <aside className="dwn-aside" aria-label="What Cobrain holds">
      {who?.kind === 'new'
        ? (
          <>
            <section className="dwn-side"><h3>Nothing on record yet</h3><p className="dwn-note">{addrSays && !addrSays.use ? addrSays.text : 'Type the address and Cobrain says what it already holds with its domain.'}</p></section>
            <section className="dwn-side">
              <h3>Its record will hold</h3>
              <ul className="dwn-will">
                {WILL_HOLD.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v(nc.brand.trim() || nc.name.trim() || 'the company')}</span></li>)}
                <li><span className="k">Deals</span><span className="v">{n > 1 ? `These ${n}` : 'This one'}</span></li>
              </ul>
            </section>
          </>
        )
        : null}
      {side === 'rights' && property
        ? (
          <section className="dwn-side" aria-label={`${property.name} on the register`}>
            <h3>{property.name} on the register</h3>
            <ul className="dwn-will">
              <li><span className="k">Sport</span><span className="v">{property.sport}</span></li>
              {property.offeredBy ? <li><span className="k">Offered by</span><span className="v">{property.offeredBy}</span></li> : null}
              {property.territory ? <li><span className="k">Territory</span><span className="v">{property.territory}</span></li> : null}
              {property.matchesVideo !== null || property.matchesData !== null
                ? <li><span className="k">Matches</span><span className="v">{[property.matchesVideo !== null ? `${property.matchesVideo} video` : '', property.matchesData !== null ? `${property.matchesData} data` : ''].filter(Boolean).join(', ')}</span></li>
                : null}
              <li><span className="k">Ours</span><span className="v">{property.held ? 'Yes' : `Not yet${property.status ? `, ${property.status.toLowerCase()}` : ''}`}</span></li>
            </ul>
          </section>
        )
        : null}
      {who?.kind === 'company'
        ? column
          ? <CompanySide col={column} ticked={ticked} onTick={(t) => setTicked((x) => { const y = new Set(x); if (y.has(t)) y.delete(t); else y.add(t); return y; })} onMore={moreThreads} />
          : <section className="dwn-side"><p className="dwn-note" role="status">{col?.failed ? `Cobrain could not read ${who.name} just now. The deal can still be added.` : `Reading ${who.name}`}</p></section>
        : null}
      {more.length > 0
        ? (
          <section className="dwn-side">
            <h3>More from {who?.kind === 'company' ? who.name : ''}</h3>
            <ul className="dwn-dl">
              {more.slice(0, HOLDER_SHOWN).map((p) => <li key={p.id}><span className="nm">{p.name}</span><span className="st">{p.held ? 'Ours' : p.status ?? ''}</span></li>)}
            </ul>
            {more.length > HOLDER_SHOWN ? <p className="dwn-note">{HOLDER_SHOWN} of {more.length} shown.</p> : null}
          </section>
        )
        : null}
      {!who ? <section className="dwn-side"><p className="dwn-note">Choose the company, and what Cobrain holds on it shows here.</p></section> : null}
    </aside>
  );

  return (
    <div className="deals dwn">
      <Head open={open} side={side} onSide={switchSide} />
      <div className="dwn-split">
        <section className="dwn-form" aria-label="Add a deal" ref={formRef}>
          {whoBlock}
          {side === 'sales' ? salesWhat : rightsWhat}
          <h3 className="dwn-h">{several ? 'Where each stands, and next' : 'Where it stands, and next'}</h3>
          {rowsTable}
          {stageAndNext}
          {pays}
          {foot}
        </section>
        {aside}
      </div>
    </div>
  );
}

function Head({ open, side, onSide }: { open: (path: string) => void; side: Side; onSide: (s: Side) => void }) {
  return (
    <>
      <nav className="dwn-crumbs" aria-label="Back"><ActionButton reading type="button" className="hx-link m" onClick={() => open('/deals')}>Deals</ActionButton></nav>
      <div className="dwn-head">
        <h1 className="dl-title">Add a deal</h1>
        <Seg label="Which side" value={side} onChange={onSide} options={[['sales', 'Selling to a client'], ['rights', 'Acquiring rights']]} />
      </div>
    </>
  );
}
